import type { FilterLeaf } from '@lens/shared';
import { isCurrentUserToken } from '@lens/shared';
import type { FieldDefinition } from '@lens/shared';

const AUTOFILL_FIELD_TYPES = new Set([
  'text',
  'textarea',
  'markdown',
  'url',
  'number',
  'select',
  'multi_select',
  'boolean',
  'date',
  'reference',
]);

/**
 * Извлекает дефолтные значения для новой записи из фильтров представления (FR-4.1).
 *
 * Правила:
 * - Поддерживаются операторы `eq` и `in`. `!=`, `>`, `<`, `contains` — игнорируются.
 * - Для `in` берём массив целиком: для multi_select / reference(multiple) это и есть
 *   ожидаемое значение поля; для одиночных полей берём первый элемент массива
 *   (фильтр «один из набора» имеет смысл только если набор из одного значения).
 * - Поле должно существовать в типе записи и поддерживать автозаполнение
 *   (computed и другие неподдерживаемые типы исключены).
 * - Условия, связанные через `or`, не автозаполняются (значение неоднозначно).
 * - При нескольких подходящих фильтрах для одного поля побеждает первое совпадение.
 */
export function defaultsFromFilters(
  filters: FilterLeaf[],
  fields: FieldDefinition[],
): Record<string, unknown> {
  const fieldBySlug = new Map(fields.map((f) => [f.slug, f]));
  const result: Record<string, unknown> = {};

  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i]!;
    if (i > 0 && filter.combinator === 'or') continue;
    if (filter.operator !== 'eq' && filter.operator !== 'in') continue;
    if (filter.value === undefined || filter.value === null) continue;

    const field = fieldBySlug.get(filter.fieldSlug);
    if (!field) continue;
    if (!AUTOFILL_FIELD_TYPES.has(field.fieldType)) continue;
    if (filter.fieldSlug in result) continue;

    const isMultiValueField =
      field.fieldType === 'multi_select' ||
      (field.fieldType === 'reference' && (field.config as { multiple?: boolean } | null)?.multiple === true);

    let value: unknown = filter.value;
    if (filter.operator === 'in') {
      if (!Array.isArray(filter.value) || filter.value.length === 0) continue;
      value = isMultiValueField ? filter.value : filter.value[0];
    } else {
      // eq
      if (isMultiValueField) {
        // eq на multi_select — единственное значение, оборачиваем в массив,
        // чтобы UI-контрол с mode="multiple" получил ожидаемую форму.
        value = Array.isArray(filter.value) ? filter.value : [filter.value];
      } else if (field.fieldType === 'reference' && typeof filter.value !== 'string') {
        continue;
      }
    }

    // Подстановка «текущий пользователь» — не значение поля, а токен, который
    // раскрывается только на сервере. В форму его класть нельзя (сохранился бы
    // литерал «@me»); нужного человека подставляет отдельный резолв, см.
    // `currentUserFilterSlugs` и previewAutofillCurrentUserRefs в SpacePage.
    if (Array.isArray(value) ? value.some(isCurrentUserToken) : isCurrentUserToken(value)) continue;

    result[filter.fieldSlug] = value;
  }

  return result;
}

/**
 * Слаги полей, которые фильтры приравнивают к текущему пользователю
 * (`= я` / `в списке [я]`). Из них форма создания заполняет поле самим
 * пользователем: создавая запись в представлении «Мои задачи», человек
 * ожидает увидеть себя исполнителем.
 */
export function currentUserFilterSlugs(
  filters: FilterLeaf[],
  fields: FieldDefinition[],
): string[] {
  const fieldBySlug = new Map(fields.map((f) => [f.slug, f]));
  const slugs: string[] = [];
  for (let i = 0; i < filters.length; i++) {
    const filter = filters[i]!;
    if (i > 0 && filter.combinator === 'or') continue;
    if (filter.operator !== 'eq' && filter.operator !== 'in') continue;
    const v = filter.value;
    const usesToken = Array.isArray(v) ? v.some(isCurrentUserToken) : isCurrentUserToken(v);
    if (!usesToken) continue;
    const field = fieldBySlug.get(filter.fieldSlug);
    if (!field) continue;
    if (field.fieldType !== 'reference' && field.fieldType !== 'user') continue;
    if (!slugs.includes(field.slug)) slugs.push(field.slug);
  }
  return slugs;
}

/**
 * Извлекает значения по умолчанию из FieldDefinition.config:
 * — `defaultValue` любого поля (если задано в настройках типа);
 * — для статусного select (`hasCategories=true`) — опция с категорией `backlog`,
 *   если defaultValue не задан явно.
 *
 * Эти дефолты подставляются в форму создания, чтобы пользователь видел
 * стартовое значение до сабмита (на API уже применяются те же правила,
 * см. `apps/api/src/modules/items/service.ts`).
 *
 * computed-поля игнорируются.
 */
const TEXT_FIELD_TYPES = new Set(['text', 'textarea', 'markdown', 'url']);

function optionValues(config: unknown): Set<string> {
  const options = (config as { options?: unknown } | null)?.options;
  if (!Array.isArray(options)) return new Set();
  return new Set(
    options
      .map((o) => (o && typeof o === 'object' ? (o as { value?: unknown }).value : undefined))
      .filter((v): v is string => typeof v === 'string'),
  );
}

/**
 * Переносит уже введённые в форме создания значения при смене типа записи
 * (до сохранения). Зеркалит серверную логику `changeItemType`
 * (`apps/api/src/modules/items/service.ts`): значение остаётся, если в целевом
 * типе есть поле с тем же slug и совместимым типом.
 *
 * - текстовые типы (text/textarea/markdown/url) взаимозаменяемы;
 * - select/multi_select — только если значение есть среди опций целевого поля
 *   (на этапе создания не делаем remap по label — пользователь переберёт сам);
 * - reference — только при совпадении словаря и кратности;
 * - number/date/boolean — при совпадении типа;
 * - computed и slug'и, которых нет в целевом типе, отбрасываются.
 *
 * Это важно не только для UX: бэкенд `createItem` бросает `field_not_found`
 * на любой лишний slug, поэтому форму нельзя сабмитить с «хвостами» прежнего типа.
 */
export function carryFieldsToType(
  fields: Record<string, unknown>,
  fromFields: FieldDefinition[],
  toFields: FieldDefinition[],
): Record<string, unknown> {
  const fromBySlug = new Map(fromFields.map((f) => [f.slug, f]));
  const result: Record<string, unknown> = {};

  for (const tgt of toFields) {
    if (tgt.fieldType === 'computed') continue;
    const value = fields[tgt.slug];
    if (value === undefined || value === null) continue;
    const src = fromBySlug.get(tgt.slug);
    if (!src) continue;

    if (TEXT_FIELD_TYPES.has(tgt.fieldType) && TEXT_FIELD_TYPES.has(src.fieldType)) {
      result[tgt.slug] = value;
      continue;
    }
    if (src.fieldType !== tgt.fieldType) continue;

    switch (tgt.fieldType) {
      case 'select': {
        if (typeof value === 'string' && optionValues(tgt.config).has(value)) result[tgt.slug] = value;
        break;
      }
      case 'multi_select': {
        const allowed = optionValues(tgt.config);
        const kept = Array.isArray(value) ? value.filter((v) => typeof v === 'string' && allowed.has(v)) : [];
        if (kept.length > 0) result[tgt.slug] = kept;
        break;
      }
      case 'reference': {
        const sc = (src.config ?? {}) as { dictionaryId?: string; multiple?: boolean };
        const tc = (tgt.config ?? {}) as { dictionaryId?: string; multiple?: boolean };
        if (sc.dictionaryId && sc.dictionaryId === tc.dictionaryId && !!sc.multiple === !!tc.multiple) {
          result[tgt.slug] = value;
        }
        break;
      }
      default:
        result[tgt.slug] = value;
    }
  }

  return result;
}

/**
 * ITEM-13: значения для полей с config.autofillCurrentUser === true.
 *
 * Синхронно (для превью в форме создания) можно заполнить только user-поля —
 * их значение и есть id пользователя. Для reference-полей нужен резолв
 * логина/email в запись справочника: авторитетно это делает бэкенд в
 * `createItem`, а фронт делает best-effort превью асинхронно (SpacePage).
 */
export function autofillCurrentUserDefaults(
  fields: FieldDefinition[],
  currentUserId: string | null | undefined,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!currentUserId) return result;
  for (const field of fields) {
    if (field.fieldType !== 'user') continue;
    const cfg = (field.config ?? {}) as Record<string, unknown>;
    if (cfg.autofillCurrentUser !== true) continue;
    result[field.slug] = cfg.multiple === true ? [currentUserId] : currentUserId;
  }
  return result;
}

export function defaultsFromFieldConfigs(fields: FieldDefinition[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const field of fields) {
    if (field.fieldType === 'computed') continue;
    const cfg = (field.config ?? {}) as Record<string, unknown>;

    if (field.fieldType === 'select' && cfg.hasCategories === true && cfg.defaultValue === undefined) {
      const options = (cfg.options as Array<{ value?: unknown; category?: unknown }> | undefined) ?? [];
      const backlog = options.find(
        (opt) => opt && typeof opt === 'object' && opt.category === 'backlog' && typeof opt.value === 'string',
      );
      if (backlog && typeof backlog.value === 'string') {
        result[field.slug] = backlog.value;
        continue;
      }
    }

    if (cfg.defaultValue !== undefined) {
      result[field.slug] = cfg.defaultValue;
    }
  }
  return result;
}
